/* ManageRolesRenderer.js */

"use strict";
/* global Slick */

const _ = require("lodash");
const formatter = require("./cellFormaters");
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const editDialog = require("./veil/addEditUserRole/AddEditUserRoleComponent");
const newDialog = require("./veil/addNewUserRole/AddNewUserRoleComponent");

class ManageRolesRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("manage-roles-gl");
        this._rootElem.component = this._model;

        this._headerElem = document.createElement("h1");
        this._headerElem.innerText = "Roles";
        this._rootElem.appendChild(this._headerElem);

        this._noDataMessage = document.createElement("span");
        this._noDataMessage.innerText = "No Roles Available";
        this._noDataPlaceHolder = this._createNoDataPlaceholder(this._noDataMessage);

        this._addNewRoleButton = new ButtonControl({
            label: "Add Role +",
            active: true,
            class: "add-new-role",
        });

        this.buttonContainer = document.createElement("div");
        this.buttonContainer.classList.add("add-new-row");
        this.buttonContainer.appendChild(this._addNewRoleButton.renderer.getRootElement());

        this._clickHandler = _.debounce(this._clickHandler.bind(this), 500, {
            leading: true,
            trailing: false,
        });
        this._fitTableSize = this._fitTableSize.bind(this);

        this._registerListeners();

        this._tableOptions = {
            editable: true,
            enableAddRow: false,
            enableCellNavigation: true,
            enableColumnReorder: false,
            forceFitColumns: true,
            enableAsyncPostRender: true,
            asyncEditorLoading: false,
            rowHeight: 45,
            autoHeight: true,
        };

        this._roleContainer = document.createElement("role-container");
        this._slickTableContainer = document.createElement("user-roles-table");
        this._slickWrapper = document.createElement("user-roles-table-wrapper");
        this._slickWrapper.appendChild(this._slickTableContainer);
        this._slickTable = undefined;
        this._roleContainer.appendChild(this._slickWrapper);

        this._rootElem.appendChild(this._slickWrapper);

        this._columns = [
            {
                id: "enabled",
                width: 65,
                name: "Enabled",
                toolTip: "When checked, the role can be assigned to users in the organization",
                field: "enabled",
                resizable: false,
                formatter: formatter.checkboxFormatter,
                cssClass: "centered",
                headerCssClass: "centered",
            },
            {
                id: "shared",
                width: 60,
                name: "Shared",
                toolTip: "When checked, the role is available in child organizations",
                field: "shared",
                resizable: false,
                formatter: formatter.checkboxFormatter,
                cssClass: "centered",
                headerCssClass: "centered",
            },
            {
                id: "name",
                width: 200,
                name: "Name",
                toolTip: "Name of the role",
                field: "name",
                resizable: false,
                formatter: formatter.nameFormatter,
            },
            {
                id: "description",
                width: 200,
                name: "Description",
                toolTip: "Description of the role",
                field: "description",
                formatter: formatter.descriptionFormatter,
            },
            {
                id: "actions",
                width: 170,
                name: "Actions",
                toolTip: "View, edit, and remove roles",
                field: "hasEditRights",
                resizable: false,
                cssClass: "actions",
                headerCssClass: "centered",
                formatter: formatter.actionButtonsFormatter(this._model),
                asyncPostRender: this._renderButtonsContainer.bind(this),
            },
        ];
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        this._unregisterListeners();

        let promArr = [];

        if (this._slickTable && this._slickTable.destroy) {
            this._slickTable.destroy();
        }

        return Promise.all(promArr).then(() => {
            this._model = undefined;
            this._headerElem.remove();
            this._headerElem = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            this._noDataMessage.remove();
            this._noDataMessage = undefined;

            this._noDataPlaceHolder = undefined;
            this.buttonContainer = undefined;
            this._clickHandler = undefined;
            this._fitTableSize = undefined;
            this._tableOptions = undefined;
            this._roleContainer = undefined;
            this._slickTableContainer = undefined;
            this._slickTable = undefined;
            this._columns = undefined;
        });
    }
    _registerListeners() {
        this._addNewRoleButton.on("UserClick", this._addNewRoleButtonClick.bind(this));
        document.addEventListener("click", this._clickHandler);
        this._model.on("feedUpdated", this._onFeedUpdated.bind(this));
        this._model.on("OrganizationChanged", this._onOrganizationChanged.bind(this));

        window.addEventListener("resize", this._fitTableSize);
    }
    _unregisterListeners() {
        this._addNewRoleButton.removeAllListeners("UserClick");
        document.removeEventListener("click", this._clickHandler);
        this._model.removeListener("feedUpdated", this._onFeedUpdated);
        this._model.removeListener("OrganizationChanged", this._onOrganizationChanged);

        window.removeEventListener("resize", this._fitTableSize);
    }

    _createNoDataPlaceholder(noDataMessage) {
        const noDataPlaceholder = document.createElement("div");
        noDataPlaceholder.classList.add("no-data-placeholder");
        noDataPlaceholder.appendChild(noDataMessage);
        return noDataPlaceholder;
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (this.model) {
            return this.model.whenModelIsReady().then(() => {
                if (!this._slickTableDataView) {
                    this._slickTableDataView = new Slick.Data.DataView();
                }
                if (_.isEmpty(this._slickTable)) {
                    this._slickTableDataView.setItems(this.model.slickData);
                    this.sortTable();
                    this.iterateGridRows();

                    this._slickTable = new Slick.Grid(
                        this._slickTableContainer,
                        this._slickTableDataView,
                        this._columns,
                        this._tableOptions
                    );
                } else {
                    this.setSlickGridTableData(this.model.slickData);
                }
                if (!this._slickTableContainer.contains(this._noDataPlaceHolder)) {
                    this._slickTableContainer.appendChild(this._noDataPlaceHolder);
                }

                this._slickTableContainer.appendChild(this.buttonContainer);
                //this.setSlickGridTableData(this._slickTableDataView);
            });
        }
    }

    _renderButtonsContainer(cellNode, row) {
        const rowData = this._slickTableDataView.getItemByIdx(row);
        if (_.get(rowData, "seeded", false) || _.get(rowData, "isInherited", false)) {
            cellNode[0].children[1].setAttribute("disabled", true);
            //cellNode[0].children[1].classList.add("hidden");
            cellNode[0].children[0].innerText = "View";
        }
        if (!_.get(rowData, "hasEditRights", false)) {
            cellNode[0].children[0].innerText = "View";
        }
    }

    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START PRIVATE RENDER METHODS */
    /*******************************************/

    setSlickGridTableData(newData) {
        if (newData && newData.length > 0) {
            //we expect a non-empty array to process
            return new Promise((resolve) => {
                this._slickTableDataView.setItems(newData);
                this.sortTable();
                //this.iterateGridRows();
                this._slickTable.invalidate();
                this._slickTable.render();
                this.checkIsEmpty();
                this._fitTableSize(); // fix windows os slickgrid resize issue
                return resolve();
            });
        }
    }

    _fitTableSize() {
        if (this._slickTable) {
            this._slickTable.resizeCanvas();
        }
    }

    _openRoleEditor(entity) {
        let component;
        if (_.isEmpty(entity)) {
            component = new newDialog({
                manageRoleComponent: this.model,
                entityDef: this.model._actionGroupEntityDef,
                userFeedback: this.model.userFeedback,
                activeOrgId: this.model.activeOrgId,
                customFieldsWithInherited: this.model._customFieldsWithInherited,
                deviceEntity: this.model._DeviceEntityClass,
                uiUtils: this.model._uiUtils,
                descendantOrgs: this.model._descendantOrgs,
            });
        } else {
            component = new editDialog({
                manageRoleComponent: this.model,
                entityDef: this.model._actionGroupEntityDef,
                actionGroupEntity: entity,
                userFeedback: this.model.userFeedback,
                descendantOrgs: this.model._descendantOrgs,
                activeOrgId: this.model.activeOrgId,
                customFieldsWithInherited: this.model._customFieldsWithInherited,
                deviceEntity: this.model._DeviceEntityClass,
                uiUtils: this.model._uiUtils,
            });
        }
        this._modelDialogOpen(component);
    }

    _modelDialogOpen(component) {
        this.model.userFeedback.displayCustom(component, {notDismissable: false}).catch((error) => {
            if (error && !error.userRejected) {
                this._logger.warn(
                    {error: error},
                    "ManageRolesRenderer._openRoleEditor() Error in _openRoleEditor"
                );
            }
        });
    }

    iterateGridRows() {
        this._slickTableDataView.getItemMetadata = ((original_getItemMetadata) => (row) => {
            const item = this._slickTableDataView.getItem(row);
            let ret = original_getItemMetadata(row);
            if (item.isInherited || item.seeded) {
                ret = ret || {};
                ret.cssClasses = (ret.cssClasses || "") + " " + "readonly-row";
            }
            return ret;
        })(this._slickTableDataView.getItemMetadata);
    }

    sortTable(sortByField) {
        sortByField = sortByField || "name";
        if (this._slickTableDataView) {
            this._slickTableDataView.sort(this.getSortingComparator(sortByField));
        }
    }

    getSortingComparator(sortField = "name") {
        const fieldCompare = (aItem, bItem) => {
            const a = aItem[sortField];
            const b = bItem[sortField];
            if (_.isNumber(a) && _.isNumber(b)) {
                return a - b;
            }
            return String(a).localeCompare(String(b));
        };

        // sort between seeded and inherited roles alphabatically
        const isFirst = (item) => Boolean(item.isInherited || item.seeded);
        const firstCompare = (a, b) => (isFirst(a) === isFirst(b) ? 0 : isFirst(a) ? -1 : 1);
        return function comparator(a, b) {
            return firstCompare(a, b) || fieldCompare(a, b);
        };
    }

    _editActionGroup(data) {
        Promise.all([
            this._model._retrieveActionGroup(data.id),
            this._model.retrieveCustomFieldsEntity(this._model.activeOrgId),
        ]).then((values) => {
            this._model._customFieldsWithInherited = values[1];
            this._openRoleEditor(values[0]);
        });
    }

    _deleteActionGroup(data) {
        const deleteMessage = `Are you sure you want to delete the role "${data.name}"?`;
        return this.model.userFeedback
            .displayConfirmation(deleteMessage, {
                title: "Delete",
                confirmDetails: {
                    text: "Confirm",
                    type: "destructive",
                },
                rejectDetails: {
                    text: "Cancel",
                    type: "text",
                },
                notDismissable: true,
                dontHide: true,
            })
            .then(() => {
                const loadingVeil = () =>
                    this.model.userFeedback.displayLoading({
                        message: "Deleting the role... This could take a moment",
                    });
                return Promise.all([loadingVeil(), this.model.deleteActionGroup(data.id)]);
            })
            .catch((error) => {
                if (error && !error.userRejected) {
                    console.log("Error in Delete: _deleteActionGroup()", error);
                    return this._model && this.model.userFeedback.displayError(error);
                }
            })
            .then(() => this._model && this.model.userFeedback.hideDisplayed());
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    _addNewRoleButtonClick() {
        this._model
            .retrieveCustomFieldsEntity(this._model.activeOrgId)
            .then((customFieldEntity) => {
                this._model._customFieldsWithInherited = customFieldEntity;
                this._openRoleEditor(null);
            });
    }

    _clickHandler(e) {
        //if we click delete column
        const target = e.target;
        if (target.getAttribute("role") === "button" && !target.getAttribute("disabled")) {
            const row = target.getAttribute("row");
            const data = this._slickTableDataView.getItemByIdx(row);

            switch (target.getAttribute("action")) {
                case "edit":
                    this._editActionGroup(data);
                    break;
                case "delete":
                    this._deleteActionGroup(data);
                    break;
                default:
                    break;
            }
        }
        e.stopPropagation();
    }

    _onFeedUpdated() {
        if (this._model) {
            this._model
                ._convertRoleDataToSlickTableFormat(
                    _.get(this._model.actionGroupsFeed, "cache", {})
                )
                .then(() => {
                    this.setSlickGridTableData(this._model.slickData);
                });
        }
    }

    _onOrganizationChanged() {
        this._model._resetPromises();
        this.render();
    }

    _setIsEmpty(state) {
        this._slickTableContainer.classList.toggle("empty", !!state);
    }

    checkIsEmpty() {
        const gridData = this.model.slickData;
        this._setIsEmpty(!gridData.length);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    //TODO - Nobody should be using methods on a Renderer directly so make all these methods private
    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}
module.exports = ManageRolesRenderer;
